import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, MessageSquarePlus, Star } from "lucide-react";
import { API } from "@/App";

// Carrousel des avis clients (vrais avis publiés) : une carte façon « embed » Discord
// glisse de la gauche vers le milieu, reste quelques secondes, puis repart vers la
// droite pendant que la suivante arrive. Pause au survol. Sans avis : invitation à
// donner le sien.

const HOLD_MS = 5000;
const SLIDE_MS = 700;
const ACCENTS = ["#38bdf8", "#8b5cf6", "#f472b6", "#34d399", "#f59e0b"];

function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${value >= n ? "fill-yellow-400 text-yellow-400" : "text-white/15"}`} />)}
    </span>
  );
}

function Avatar({ review }) {
  const [broken, setBroken] = useState(false);
  const name = review.username || "Client";
  if (review.avatar && !broken) {
    return <img src={review.avatar} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} className="w-10 h-10 rounded-full object-cover ring-2 ring-white/10" />;
  }
  return <span className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center font-semibold">{name.slice(0, 1).toUpperCase()}</span>;
}

export function ReviewCard({ review, accent }) {
  return (
    <article className="relative w-full rounded-2xl bg-[#13141c]/95 border border-white/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.45)] overflow-hidden" data-testid="review-card">
      <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: accent }} aria-hidden="true" />
      <div className="pl-6 pr-5 py-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45 truncate">{review.product_name || "VantaPulse"}</p>
        {review.title && <h3 className="mt-2 font-heading text-lg font-bold leading-snug line-clamp-2">{review.title}</h3>}
        <div className="mt-2 flex items-center gap-2"><Stars value={review.rating} /><span className="text-xs text-white/50">{review.rating}/5</span></div>
        {review.comment && <p className="mt-3 text-sm leading-relaxed text-white/70 line-clamp-4 whitespace-pre-line">{review.comment}</p>}
        <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center gap-3">
          <Avatar review={review} />
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{review.username || "Client"}</p>
            <p className="text-[11px] text-white/40 flex items-center gap-1.5">
              {review.verified && <span className="inline-flex items-center gap-1 text-emerald-400"><BadgeCheck className="w-3.5 h-3.5" />Achat vérifié</span>}
              {review.verified && <span>·</span>}
              <span>{new Date(review.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ReviewCarousel({ productId, title = "Ce que nos clients en disent", subtitle, emptyLink, emptyText }) {
  const [reviews, setReviews] = useState(null);
  const [stats, setStats] = useState(null);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("in"); // in → hold → out
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const params = { limit: 30, ...(productId ? { product_id: productId } : {}) };
    axios.get(`${API}/reviews`, { params }).then((r) => setReviews((r.data || []).filter((x) => x.comment || x.title))).catch(() => setReviews([]));
    axios.get(`${API}/reviews/stats`, { params: productId ? { product_id: productId } : {} }).then((r) => setStats(r.data)).catch(() => {});
  }, [productId]);

  const count = reviews?.length || 0;
  // Enchaînement : entrée (glisse depuis la gauche) → pause → sortie (glisse à droite) → suivante.
  useEffect(() => {
    if (count < 2 && phase === "hold") return undefined;
    if (paused && phase === "hold") return undefined;
    const delay = phase === "in" ? SLIDE_MS : phase === "hold" ? HOLD_MS : SLIDE_MS;
    const id = setTimeout(() => {
      if (phase === "in") setPhase("hold");
      else if (phase === "hold") setPhase("out");
      else { setIndex((i) => (i + 1) % Math.max(1, count)); setPhase("in"); }
    }, delay);
    return () => clearTimeout(id);
  }, [phase, paused, count]);

  const review = count ? reviews[index % count] : null;
  const accent = useMemo(() => ACCENTS[index % ACCENTS.length], [index]);

  if (reviews === null) return null;
  return (
    <section className="relative" data-testid="review-carousel">
      <style>{`
        @keyframes vp-review-in { from { transform: translateX(-110%) rotate(-2deg); opacity: 0; } to { transform: translateX(0) rotate(0); opacity: 1; } }
        @keyframes vp-review-out { from { transform: translateX(0) rotate(0); opacity: 1; } to { transform: translateX(110%) rotate(2deg); opacity: 0; } }
        .vp-review-in { animation: vp-review-in ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        .vp-review-out { animation: vp-review-out ${SLIDE_MS}ms cubic-bezier(0.55, 0, 0.78, 0) both; }
        @media (prefers-reduced-motion: reduce) {
          @keyframes vp-review-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes vp-review-out { from { opacity: 1; } to { opacity: 0; } }
        }
      `}</style>
      <div className="text-center mb-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold">{title}</h2>
        {stats?.count > 0 ? (
          <p className="mt-2 text-white/55 text-sm flex items-center justify-center gap-2"><Stars value={Math.round(stats.average)} /><span>{stats.average.toFixed(1)} / 5 · {stats.count} avis vérifiés</span></p>
        ) : subtitle && <p className="mt-2 text-white/55 text-sm">{subtitle}</p>}
      </div>

      {review ? (
        <div className="relative mx-auto max-w-xl overflow-hidden px-2 py-6" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} aria-live="polite">
          <div key={`${review.id}-${index}`} className={phase === "out" ? "vp-review-out" : phase === "in" ? "vp-review-in" : ""}>
            <ReviewCard review={review} accent={accent} />
          </div>
          {count > 1 && (
            <div className="mt-5 flex justify-center gap-1.5" role="tablist" aria-label="Avis">
              {reviews.slice(0, 12).map((r, i) => (
                <button key={r.id} type="button" role="tab" aria-selected={i === index % count} aria-label={`Avis ${i + 1}`}
                  onClick={() => { setIndex(i); setPhase("in"); }}
                  className={`h-1.5 rounded-full transition-all ${i === index % count ? "w-6 bg-white/80" : "w-1.5 bg-white/20 hover:bg-white/40"}`} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mx-auto max-w-xl rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center" data-testid="review-carousel-empty">
          <MessageSquarePlus className="w-7 h-7 text-sky-300 mx-auto" />
          <p className="mt-3 font-semibold">Sois le premier à donner ton avis</p>
          <p className="mt-1 text-sm text-white/50">{emptyText || "Tu as acheté un de nos produits ? Dis-nous ce que tu en penses."}</p>
          {emptyLink && (emptyLink.startsWith("#")
            ? <a href={emptyLink} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-violet-600 text-sm font-semibold">Laisser un avis</a>
            : <Link to={emptyLink} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-violet-600 text-sm font-semibold">Voir nos produits</Link>)}
        </div>
      )}
    </section>
  );
}
